// TODO(Sergio): Temporary skip eslint rules, this is part of the A6-A12 migration from mono-frontend
/* eslint-disable  */
import { Injectable } from '@angular/core';
import { DayEntity } from './../models/calendar-single.interfaces';

/**
 * Date service for working with calendar widgets
 * and any other services.
 */
@Injectable({
  providedIn: 'root'
})
export class DateService {
  private monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  private daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  private startDateOfTheMonth = 1;

  dateStringToDate(
    dateString = `${
      new Date().getMonth() + 1
    }/${new Date().getDate()}/${new Date().getFullYear()}`,
    offSetMonth = 0,
    offSetYear = 0
  ): Date {
    const dateArray = dateString.split('/').map((value) => parseInt(value, 10));
    // Create a new date object, including any offsets.
    const date = new Date(
      dateArray[2] + offSetYear,
      dateArray[0] - 1 + offSetMonth,
      this.startDateOfTheMonth
    );
    return date;
  }

  getFormattedYear(
    offSetMonth = 0,
    offSetYear = 0,
    dateString = `${
      new Date().getMonth() + 1
    }/${new Date().getDate()}/${new Date().getFullYear()}`
  ): string {
    const dateArray = dateString.split('/').map((value) => parseInt(value, 10));
    // Create a new date object, including any offsets.
    const date = new Date(
      dateArray[2] + offSetYear,
      dateArray[0] - 1 + offSetMonth,
      dateArray[1]
    );
    return date.getFullYear().toString();
  }

  getFormattedMonth(
    offSetMonth = 0,
    offSetYear = 0,
    dateString = `${
      new Date().getMonth() + 1
    }/${new Date().getDate()}/${new Date().getFullYear()}`
  ): string {
    const dateArray = dateString.split('/').map((value) => parseInt(value, 10));
    // Create a new date object, including any offsets.
    const date = new Date(
      dateArray[2] + offSetYear,
      dateArray[0] - 1 + offSetMonth,
      this.startDateOfTheMonth
    );
    return this.monthNames[date.getMonth()];
  }

  /**
   * Get last week from the previous month,
   * this generates the disabled days at the beginning of the calendar.
   */
  getPreviousMonthLastWeek(date = new Date()): (DayEntity | null)[] {
    // Array to hold the previous days.
    const previousDays: (DayEntity | null)[] = [];
    // Holds the day of the week, 0 to 6
    let dayOfWeek: number;
    // Create a new object so we don't update the reference from the function.
    const previousDate = new Date(date.toString());

    // Set the date to the last day of the previous month.
    previousDate.setMonth(previousDate.getMonth(), 0);

    // Get the day of the week for the last day of the previous month (0 - 6).
    dayOfWeek = previousDate.getDay();

    // If it's a Saturday (6) return an empty array, since there is no week to display on the calendar.
    if (dayOfWeek === 6) {
      return [];
    }

    // Go backwards (from N to 0) on the day of the week and get all the days.
    for (let i = dayOfWeek; i >= 0; i -= 1) {
      previousDays.push({
        type: 'previous',
        label: previousDate.getDate().toString(),
        value: `${
          previousDate.getMonth() + 1
        }/${previousDate.getDate()}/${previousDate.getFullYear()}`,
        aria: `${previousDate.getDate()} ${
          this.monthNames[previousDate.getMonth()]
        } ${previousDate.getFullYear()}, ${
          this.daysOfWeek[previousDate.getDay()]
        }`
      });
      previousDate.setDate(previousDate.getDate() - 1);
    }

    return previousDays;
  }

  /**
   * Get the first week for the next month.
   * This generates the available days for the next month.
   */
  getNextMonthFirstWeek(date = new Date()): (DayEntity | null)[] {
    // Setup an array to hold all of the days.
    const firstWeekDays: (DayEntity | null)[] = [];
    // Create a new date object so we don't modify the one passed in by the function.
    const nextDate = new Date(date.toString());
    // Setup the day of week.
    let dayOfWeek: number;

    // Set this date to the first day of next month.
    nextDate.setMonth(nextDate.getMonth() + 1, 1);

    // Get the day of week.
    dayOfWeek = nextDate.getDate();

    // If it's Sunday, return an empty array (no days to display).
    if (dayOfWeek === 0) {
      return [];
    }

    // Loop forwards till the last day of the week.
    for (let i = dayOfWeek; i <= 6; i++) {
      firstWeekDays.push({
        type: 'next',
        label: nextDate.getDate().toString(),
        value: `${
          nextDate.getMonth() + 1
        }/${nextDate.getDate()}/${nextDate.getFullYear()}`,
        aria: `${nextDate.getDate()} ${
          this.monthNames[nextDate.getMonth()]
        } ${nextDate.getFullYear()}, ${this.daysOfWeek[nextDate.getDay()]}`
      });
      nextDate.setDate(nextDate.getDate() + 1);
    }

    return firstWeekDays;
  }

  /**
   * Get all of the days of the month for the date given.
   */
  getMonth(date = new Date()): (DayEntity | null)[] {
    // Setup the array of days for the month.
    const days: (DayEntity | null)[] = [];
    // Get the last day of the month.
    const lastDayDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    // Get the first day of the month.
    const firstDayDate = new Date(date.getFullYear(), date.getMonth(), 1);
    // Get the total days in the month.
    const totalDays = lastDayDate.getDate();

    // Loop over all days and add the details to an array.
    for (let i = 1; i <= totalDays; i++) {
      days.push({
        type: 'current',
        label: firstDayDate.getDate().toString(),
        value: `${
          firstDayDate.getMonth() + 1
        }/${firstDayDate.getDate()}/${firstDayDate.getFullYear()}`,
        aria: `${firstDayDate.getDate()} ${
          this.monthNames[firstDayDate.getMonth()]
        } ${firstDayDate.getFullYear()}, ${
          this.daysOfWeek[firstDayDate.getDay()]
        }`
      });
      firstDayDate.setDate(firstDayDate.getDate() + 1);
    }

    return days;
  }

  /**
   * Get an object that represents a calendar month.
   */
  getCalendar(
    offSetMonth = 0,
    offSetYear = 0,
    dateString = `${
      new Date().getMonth() + 1
    }/${new Date().getDate()}/${new Date().getFullYear()}`
  ): (DayEntity | null)[][] {
    // Create a new date object, including any offsets.
    const date = this.dateStringToDate(dateString, offSetMonth, offSetYear);
    // Get the previous month's first week of days and reverse it to display at the beginning of the calendar.
    const previousWeek = this.getPreviousMonthLastWeek(date).reverse();
    // Get all the days of the current month.
    const currentMonth = this.getMonth(date);
    // Get next month's first week of days.
    const nextWeek = this.getNextMonthFirstWeek(date);
    // Setup the complete month object (array of weeks of days).
    let completeMonth: (DayEntity | null)[][] = [];
    // Setup a temporary week object to hold each week of days.
    let tempWeek: (DayEntity | null)[] = [];
    // Counter for how many days have been added to the current week.
    let countIndexDays = -6;
    // Counter for the last week, see how many days have been added.
    let lastWeekFilledCount = 0;

    // Start a loop for each week (6 weeks in total)
    for (let week = 0; week <= 5; week += 1) {
      // Start a loop for each day (7 days Sunday through Saturday).
      for (let day = 0; day <= 6; day += 1) {
        // If it's the first week for the calendar.
        if (week === 0) {
          // Get the day from the previous
          tempWeek = tempWeek.concat(
            this.getPreviousWeek(currentMonth, previousWeek, day)
          );
        }

        // If the total days from the previous month plus the day for the current month are less than 35 (4 weeks)
        tempWeek = tempWeek.concat(
          this.getCurrentMonth4Weeks(
            previousWeek,
            currentMonth,
            tempWeek,
            week,
            day
          )
        );

        // This month has 6 weeks, so don't fill out the last week at 5.
        tempWeek = tempWeek.concat(
          this.getCurrentMonth6Weeks(
            previousWeek,
            currentMonth,
            tempWeek,
            week,
            day
          )
        );

        // Fill out the last week if there are only 5 weeks
        if (week === 4 && previousWeek.length + currentMonth.length <= 35) {
          const lastWeek = this.getLastWeek(
            currentMonth,
            previousWeek,
            nextWeek,
            lastWeekFilledCount,
            week,
            day
          );
          tempWeek = tempWeek.concat(lastWeek.newWeek);
          lastWeekFilledCount = lastWeek.newLastWeekFilledCount;
        }
        // Fill out the last week if there are 6 weeks.
        if (week === 5 && previousWeek.length + currentMonth.length > 35) {
          const lastWeek = this.getLastWeek(
            currentMonth,
            previousWeek,
            nextWeek,
            lastWeekFilledCount,
            week,
            day
          );
          tempWeek = tempWeek.concat(lastWeek.newWeek);
          lastWeekFilledCount = lastWeek.newLastWeekFilledCount;
        }

        countIndexDays += 1;
      }
      completeMonth.push(tempWeek);
      tempWeek = [];
    }

    if (!completeMonth[5].length) {
      completeMonth = completeMonth.slice(0, 5);
    }

    return completeMonth;
  }

  private getCurrentMonth6Weeks(
    previousWeek: string | any[],
    currentMonth: string | any[],
    tempWeek: (DayEntity | null)[],
    week: number,
    day: number
  ) {
    const newWeek = [];

    if (
      previousWeek.length + currentMonth.length > 35 &&
      week !== 0 &&
      week !== 5 &&
      currentMonth[day + 7 * week - previousWeek.length]
    ) {
      newWeek.push(currentMonth[day + 7 * week - previousWeek.length]);
    }

    return newWeek;
  }

  private getCurrentMonth4Weeks(
    previousWeek: string | any[],
    currentMonth: string | any[],
    tempWeek: (DayEntity | null)[],
    week: number,
    day: number
  ) {
    const newWeek = [];

    if (
      previousWeek.length + currentMonth.length <= 35 &&
      week !== 0 &&
      week !== 4 &&
      currentMonth[day + 7 * week - previousWeek.length]
    ) {
      // This month has only 5 weeks to display, so check that it's not the first and last week and there are enough
      // days to display.
      newWeek.push(currentMonth[day + 7 * week - previousWeek.length]);
    }

    return newWeek;
  }

  private getLastWeek(
    currentMonth: any[],
    previousWeek: string | any[],
    nextWeek: any[],
    lastWeekFilledCount: number,
    week: number,
    day: number
  ) {
    const newWeek = [];
    let newLastWeekFilledCount = lastWeekFilledCount;

    if (currentMonth[day + 7 * week - previousWeek.length]) {
      newWeek.push(currentMonth[day + 7 * week - previousWeek.length]);
      newLastWeekFilledCount += 1;
    } else if (nextWeek[day - lastWeekFilledCount]) {
      newWeek.push(nextWeek[day - lastWeekFilledCount]);
    }

    return {
      newWeek,
      newLastWeekFilledCount
    };
  }

  private getPreviousWeek(
    currentMonth: any[],
    previousWeek: string | any[],
    day: number
  ) {
    const week = [];

    if (previousWeek[day]) {
      week.push(previousWeek[day]);
      // If there are no more days to display from the previous month, start displaying this month's days.
    } else if (currentMonth[day - previousWeek.length]) {
      week.push(currentMonth[day - previousWeek.length]);
    }

    return week;
  }
}
